import React, { useState, useCallback } from "react";
import { Box, Container } from "@mantine/core";
import { GTVMessage } from "../shared/util/types";
import Lobby from "../games/Lobby";
import ArtificialGame from "../games/Artificial/ArtificialGame";
import HostTimer from "../shared/components/HostTimer";
import MemerableGame from "../games/Memerable/MemerableGame";
import CAHGame from "../games/CAH/CAHGame";
import TriviaGame from "../games/TriviaGame/TriviaGame";
import TriviaLiesGame from "../games/TriviaLiesGame/TriviaLiesGame";
import { DEFAULT_THEME, Games, TESTING_ENABLED } from "../shared/util/consts";
import { getTests } from "../tests/ComponentTests";
import TransitionContainer from "../shared/components/TransitionContainer";
import DynamicText from "../shared/components/DynamicText";
import { useWebSocket } from "../shared/hooks/WebSocketHook";
import { useCastReceiver } from "../shared/hooks/CastReceiverHook";

const HostContainer: React.FC = () => {
  const [sessionID, setSessionID] = useState<string | null>(null);
  const [game, setGame] = useState(Games.Lobby);
  const [timerSeconds, setTimerSeconds] = useState(-1);
  const [gameMessage, setGameMessage] = useState<GTVMessage | null>(null);
  const handleMessage = async (msg: GTVMessage) => {
    try {
      let handled = false;
      let gameHandled = false;
      switch (msg.Type) {
        case "SessionId":
          setSessionID(msg.SessionId || null);
          handled = true;
          break;
        case "LobbyData":
          setGame(Games.Lobby);
          setGameMessage(msg);
          gameHandled = true;
          handled = true;
          break;
        case "StartTimer":
          setTimerSeconds(msg.Payload.Seconds as number);
          handled = true;
          break;
        case "StopTimer":
          if (msg.Payload?.TimeRanOut) {
            playMedia("/buzzer.mp3");
          }
          setTimerSeconds(-1);
          handled = true;
          break;
        case "Error":
          console.log(msg.Payload);
          handled = true;
          break;
        default:
          // Handle game-specific messages
          setGameMessage(msg);
          gameHandled = true;
          break;
      }
      if (!gameHandled) {
        if (handled) {
          console.log("Host Processing msg", msg);
        } else {
          console.warn("Unhandled message type: " + msg.Type);
        }
      }
    } catch (err) {
      console.log("Error handling message: " + ((err as Error).message ?? msg));
    }
  };
  const { socketConnected, sendSocketMessage } = useWebSocket(handleMessage);
  const sendHostMessage = useCallback(
    async (msg: string) => {
      !TESTING_ENABLED &&
        (await sendSocketMessage({ Type: "HostMessage", Payload: msg }));
    },
    [sendSocketMessage],
  );
  // Add ping if needed for debugging connection issues
  const { playMedia } = useCastReceiver(
    sessionID !== null && sessionID.length > 0, // We are authorized if we have a session ID
    socketConnected,
    sendSocketMessage,
  );
  const getGame = () => {
    if (TESTING_ENABLED) return getTests();
    switch (game) {
      case Games.Artificial:
        return (
          <ArtificialGame
            sendHostMessage={sendHostMessage}
            gameMessage={gameMessage}
            playMedia={playMedia}
          />
        );
      case Games.CAH:
        return (
          <CAHGame
            sendHostMessage={sendHostMessage}
            gameMessage={gameMessage}
            playMedia={playMedia}
          />
        );
      case Games.MEMErable:
        return <MemerableGame />;
      case Games.Trivia:
        return <TriviaGame />;
      case Games.TriviaLies:
        return <TriviaLiesGame />;
      default:
        return sessionID ? (
          <Lobby
            onStartGame={async (game: Games) => {
              setGame(game);
              setTimerSeconds(-1); // Reset timer when starting a new game
              setGameMessage(null); // Clear any game messages
              await sendHostMessage("GameStarted");
            }}
            gameMessage={gameMessage}
            setGameMessage={setGameMessage}
            playMedia={playMedia}
          />
        ) : (
          <Box
            style={{
              alignItems: "center",
              height: "calc(100vh)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <DynamicText style={{ fontSize: 40, marginBottom: 20 }}>
              GameNightTV is Connecting...
            </DynamicText>
            <img src="/dice.gif" alt="Loading..." width={100} />
            <DynamicText style={{ fontSize: 20, marginTop: 20 }}>
              An authorized user must cast to start the game.
            </DynamicText>
          </Box>
        );
    }
  };
  return (
    <Container
      style={{
        backgroundColor: DEFAULT_THEME.BackgroundColor,
        color: DEFAULT_THEME.TextColor,
        display: "flex",
        flexDirection: "column",
        width: "calc(100vw- 40px)",
        height: "calc(100vh)",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {/* {ping && ( // Ping can be added to debug
        <div
          style={{
            position: "absolute",
            top: 15,
            left: 30,
            color: "white",
          }}
        >
          Ping {ping} ms
        </div>
      )} */}
      {timerSeconds > 0 && (
        <HostTimer
          seconds={timerSeconds}
          isVisible={timerSeconds > 0}
          onTimerEnd={() => {
            setTimerSeconds(-1);
          }}
        />
      )}
      <TransitionContainer
        initiallyVisible={true}
        transitionTrigger={game}
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          margin: 0,
          padding: 0,
        }}
      >
        {getGame()}
      </TransitionContainer>
    </Container>
  );
};
export default HostContainer;
